import React, { useContext, useEffect, useRef, useState } from 'react';
import {
	Button,
	CartDrawer,
	Contact,
	ContentLoader,
	Drawer,
	ErrorCrossIcon,
	Head,
	Hint,
	HintMessage,
	MiniCartIcon,
	ProductUpsellCard,
	Toast,
	Typography,
	Wrapper,
	getDrawerTitle,
	getHintContent,
	withLocalization,
} from '@adac-mp/components';
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom';
import { useViewport } from '@adac-mp/components/src/hooks/useViewport';
import {
	CartService,
	IUpsellRequestBody,
	IVignetteOffer,
	Locales,
	OfferContext,
	VignetteContext,
	convertApiDate,
	countryUrlMap,
	fetchInitialVignetteData,
	formatPlates,
	getUrlBasePath,
	getVignetteProductDoneCollection,
	oneDayVignettes,
	setEndDate,
	setRouteLangParam,
} from '@adac-mp/shared';
import { trackFunnelView } from '@adac-mp/components/src/utils/tracking/tracking';
import { useQuery } from '@apollo/client';
import { ITypeVignetteProductDoneFields } from '@adac-mp/shared/src/contentful-types/TypeVignetteProductDone';
import { useFlowId } from '@adac-mp/components/src/hooks/useFlowId';
import { ContentfulDrawerContent } from '@adac-mp/components/src/components/ContentfulDrawerContent';

const renderButtonGroup = (
	countryUrl: string,
	navigate: NavigateFunction,
	lang: string | undefined,
	miniCartIsOpen?: boolean,
	labels?: ITypeVignetteProductDoneFields,
) => (
	<div
		className={`flex flex-col justify-center sticky left-0 right-0 bottom-0 p-1.5 gap-1.5 bg-white -mb-3 border-t border-t-gray-700 md:m-0 md:bg-transparent md:static md:w-6/12 md:flex-row md:p-0 md:self-center md:gap-4 transition-translate duration-300 ${
			miniCartIsOpen
				? '-translate-x-[calc(100%_-_24px)] pointer-events-none md:pointer-events-auto md:-translate-x-[258px]'
				: 'translate-x-0'
		}`}>
		<Button
			data-cy="Button: add new product"
			variant="secondary"
			className="flex-1"
			tracking={{
				funnel_click: {
					label: labels?.addProductLabel || 'Produkt hinzufügen',
					element: 'Product Done',
				},
			}}
			onClick={() => navigate(getUrlBasePath(`/${countryUrl}/configure${setRouteLangParam(lang)}`))}>
			{labels?.addProductLabel || 'Produkt hinzufügen'}
		</Button>
		<Button
			data-cy="Button: go to checkout"
			className="flex-1"
			tracking={{
				funnel_click: {
					label: labels?.cart?.gotToCheckout || 'Zum Checkout',
					element: 'Product Done',
				},
			}}
			onClick={() => navigate(getUrlBasePath(`/${countryUrl}/checkout${setRouteLangParam(lang)}`))}>
			{labels?.cart?.gotToCheckout || 'Zum Checkout'}
		</Button>
	</div>
);

// eslint-disable-next-line complexity
const ProductDone = () => {
	const navigate = useNavigate();
	const flowId = useFlowId() || 0;
	const {
		cartItems,
		cartPrice,
		contentfulLabels,
		selectedProducts,
		selectedSectionTolls,
		vehicleCategory,
		updateVehicleCategory,
		deleteSelectedProducts,
		deleteSelectedSectionTolls,
		updateCart,
		updateContentfulLabels,
	} = useContext(VignetteContext);
	const { allOffers, updateAllCountries, updateAllOffers, updateVignetteOffers, updateTollSectionOffers } =
		useContext(OfferContext);
	const { lang } = useParams();
	const currentLabels = contentfulLabels as ITypeVignetteProductDoneFields;
	const [miniCartIsOpen, setMiniCartIsOpen] = useState(false);
	const [apiError, setApiError] = useState<string | undefined>(undefined);
	const [cartUpdatedMessage, setCartUpdateMessage] = useState<string | undefined>(undefined);
	const [drawerIsOpen, setDrawerIsOpen] = useState<boolean>(false);
	const [recommendedProducts, setRecommendedProducts] = useState<IVignetteOffer[] | []>([]);
	const { viewport } = useViewport();
	const unmountingComponent = useRef(false);
	const countryUrl = countryUrlMap[flowId];

	const austrianProductInCart = cartItems?.some(({ offer }) => offer.supplierId === 3);
	const swissProductInCart = cartItems?.some(({ offer }) => offer.supplierId === 14);
	const slovenianProductInCart = cartItems?.some(({ offer }) => offer.supplierId === 7);

	const noAustrianVignetteInCart =
		cartItems?.findIndex(({ offer }) => offer.moduleTypeId === 2 && offer.supplierId === 3) === -1;

	const noSectionTollOrFlexInCart = cartItems?.every(
		({ offer }) => offer.moduleTypeId !== 3 && offer.moduleTypeId !== 4,
	);
	const hintMessageIsRendered = noAustrianVignetteInCart || noSectionTollOrFlexInCart;

	const { error, data, loading } = useQuery(getVignetteProductDoneCollection, {
		variables: { lang },
	});

	useEffect(() => {
		trackFunnelView({
			label: currentLabels?.headline || 'Was wollen Sie als nächstes tun?',
			element: 'Product Done',
			trackOnce: true,
		});
	}, []);

	useEffect(() => {
		if (!error) {
			updateContentfulLabels(data?.vignetteProductDoneCollection.items[0]);
		}
	}, [data]);

	useEffect(() => {
		const cart = sessionStorage.getItem('cartHash');
		const token = sessionStorage.getItem('cartToken');
		const stashedProductCount = sessionStorage.getItem('newProductsCount');

		if (selectedProducts.length) deleteSelectedProducts();
		if (selectedSectionTolls.length) deleteSelectedSectionTolls();
		if (vehicleCategory) updateVehicleCategory(undefined);

		if (stashedProductCount && !cartUpdatedMessage) {
			setCartUpdateMessage(
				`${stashedProductCount} ${
					currentLabels?.productAddedToast || 'Artikel wurden dem Warenkorb hinzugefügt.'
				}`,
			);
		}

		if (cartItems.length === 0) {
			if (cart && token) {
				CartService.getCartInfo({ body: { cartHash: cart }, token, lang: lang as Locales }).then((res) => {
					if (res.code === 200 && res.cart) {
						updateCart(res.cart);
					} else if (res.code === 401) {
						navigate(getUrlBasePath(`/session-expired${setRouteLangParam(lang)}`), { replace: true });
					}
				});
			} else {
				navigate(getUrlBasePath(`/${countryUrl}/configure${setRouteLangParam(lang)}`), { replace: true });
			}
		}

		if (allOffers.length === 0 || lang) {
			fetchInitialVignetteData(flowId, {
				navigate,
				lang: lang as Locales,
				updateVignetteOffers,
				updateAllOffers,
				updateAllCountries,
				updateTollSectionOffers,
			});
		}

		return () => {
			if (unmountingComponent.current) {
				setCartUpdateMessage(undefined);
			} else {
				unmountingComponent.current = true;
			}
		};
	}, [lang, flowId]);

	useEffect(() => {
		const cart = sessionStorage.getItem('cartHash');
		const token = sessionStorage.getItem('cartToken');
		const upsellBody = JSON.parse(sessionStorage.getItem('upsellData') || '');

		if (cart && token && upsellBody) {
			const { licenseplate, licenseplateType, moduleDetailId, checkOutDateTime, supplierIds, countryCode } =
				upsellBody as IUpsellRequestBody;
			const requestBody = {
				cartHash: cart,
				licenseplate,
				licenseplateType,
				moduleDetailId,
				member: false,
				checkOutDateTime,
				supplierIds,
			};
			CartService.getUpsell(requestBody, token).then((res) => {
				if (res.offers) {
					setRecommendedProducts(
						res.offers.map((offer) => {
							const isOneDayVignette = oneDayVignettes.some((v) => offer.serviceCatalogCode.endsWith(v));
							return isOneDayVignette
								? { ...offer, plates: formatPlates(licenseplate).split('-'), countryCode }
								: {
										...offer,
										plates: formatPlates(licenseplate).split('-'),
										countryCode,
										dates: {
											start: convertApiDate(checkOutDateTime),
											end: setEndDate(convertApiDate(checkOutDateTime), offer),
										},
								  };
						}),
					);
				}
			});
		}
	}, [cartItems]);

	const onHintClick = () => {
		setDrawerIsOpen(true);
	};

	const renderHintProductsInfoHintMessage = () => {
		let hint: Hint[] = [];

		if (noAustrianVignetteInCart && !noSectionTollOrFlexInCart) {
			hint = getHintContent(
				'additionalVignette',
				[
					{
						tip: 'Wussten Sie schon...?',
						message:
							'Die Vignette ist in Österreich auf allen Autobahnen und Schnellstraßen Pflicht. Falls Sie noch keine Österreichische Vignette besitzen, klicken Sie einfach auf “Produkt hinzufügen".',
					},
				],
				currentLabels?.hintMessages,
			);
		}

		if (noSectionTollOrFlexInCart && !noAustrianVignetteInCart) {
			hint = getHintContent(
				'additionalSectionToll',
				[
					{
						tip: 'Wussten Sie schon...?',
						message:
							'Zusätzlich zur Vignette gilt an sechs Autobahnabschnitten eine gesonderte Streckenmaut. Falls Sie noch keine Streckenmaut besitzen, klicken Sie einfach auf “Produkt hinzufügen”.',
						link: {
							text: 'Wo benötige ich eine Streckenmaut?',
							onClick: onHintClick,
						},
					},
				],
				currentLabels?.hintMessages,
				onHintClick,
			);
		}

		if (!hint.length) return null;

		return <HintMessage className="self-center mb-2" hints={hint} />;
	};

	const renderHintCountryInfoHintMessage = () => {
		const hints: Hint[][] = [];

		if (austrianProductInCart) {
			const hint = getHintContent('additionalInfoAustria', [], currentLabels?.hintMessages);

			if (hint.length) {
				hints.push(hint);
			}
		}
		if (swissProductInCart) {
			const hint = getHintContent('additionalInfoSwitzerland', [], currentLabels?.hintMessages);

			if (hint.length) {
				hints.push(hint);
			}
		}
		if (slovenianProductInCart) {
			const hint = getHintContent('additionalInfoSlovenia', [], currentLabels?.hintMessages);

			if (hint.length) {
				hints.push(hint);
			}
		}

		if (!hints.length) return null;

		return (
			<>
				{hints.map((hint, index) => (
					<HintMessage key={index} className="self-center mb-2 last:mb-0" hints={hint} />
				))}
			</>
		);
	};

	const onProductAddedToCart = () => {
		setCartUpdateMessage(`1 ${currentLabels?.productAddedToast || 'Artikel wurde hinzugefügt.'}`);
	};

	const renderUpsellProductCards = () => {
		if (!recommendedProducts.length) return null;
		const upsellBody = JSON.parse(sessionStorage.getItem('upsellData') || '');
		const transitonYear =
			new Date(upsellBody.checkOutDateTime.date).getMonth() <= 10
				? new Date(upsellBody.checkOutDateTime.date).getFullYear()
				: new Date(upsellBody.checkOutDateTime.date).getFullYear() + 1;
		const currRecommendedProducts = recommendedProducts
			.filter(
				(prod) =>
					!(Number(prod.servcatSeason) !== transitonYear && prod.moduleTypeId === 2 && prod.supplierId === 3),
			)
			.map((offer, i) => (
				<ProductUpsellCard
					key={i}
					offer={offer}
					onProductAddedToCart={onProductAddedToCart}
					onError={setApiError}
				/>
			));
		return (
			<>
				<div className="w-screen h-px bg-gray-500" />
				<Wrapper
					className={`$transition-translate duration-300 ${
						miniCartIsOpen
							? '-translate-x-[calc(100%_-_24px)] pointer-events-none md:-translate-x-[258px] md:pointer-events-auto'
							: 'translate-x-0'
					}`}>
					<div className="flex flex-col gap-1.5 mb-6">
						<Typography className="text-center" size="4xl" component="h3" color="title" variant="header">
							{currentLabels?.upsellHeading || 'Haben Sie an alles gedacht?'}
						</Typography>
						<Typography className="text-center" size="lg" component="p" color="title">
							{currentLabels?.upsellText || 'Weitere relevante Produkte für Ihre Reise finden Sie hier.'}
						</Typography>
					</div>
					<section
						className={`grid gap-3 self-center ${
							currRecommendedProducts.length === 1 ? 'md:grid-col' : 'md:grid-cols-2'
						}  md:max-w-[880px]`}>
						{currRecommendedProducts}
					</section>
				</Wrapper>
			</>
		);
	};

	return (
		<>
			{loading ? (
				<ContentLoader isLoading hasPortal={false} />
			) : (
				<>
					{apiError && (
						<Toast
							content={apiError}
							icon={<ErrorCrossIcon />}
							position="bottom"
							onToastFinished={() => setApiError(undefined)}
						/>
					)}

					<MiniCartIcon
						cartUpdatedMessage={cartUpdatedMessage}
						cartItemsCount={cartItems.length}
						setIsOpen={setMiniCartIsOpen}
						onAnimationEnd={() => setCartUpdateMessage(undefined)}
					/>
					<Wrapper
						className={`-mt-[52px] transition-translate duration-300 ${
							recommendedProducts ? '' : 'min-h-[calc(100vh_-_56px)] sm:min-h-[calc(100vh_-_98px)]'
						} ${
							miniCartIsOpen
								? '-translate-x-[calc(100%_-_24px)] pointer-events-none md:-translate-x-[258px] md:pointer-events-auto'
								: 'translate-x-0'
						}`}
						paddingTop="large">
						<Head
							wrapperStyle={{ marginBottom: hintMessageIsRendered ? 24 : 48 }}
							headline={currentLabels?.headline || 'Was wollen Sie als nächstes tun?'}
							description={
								currentLabels?.text || 'Ihr Artikel wurde fertiggestellt und dem Warenkorb hinzugefügt.'
							}
						/>
						<div className="md:mb-6 self-center md:w-1/2">
							{(flowId === 3 || flowId === 0) && renderHintProductsInfoHintMessage()}
							{(flowId === 3 || flowId === 0) && renderHintCountryInfoHintMessage()}
						</div>
						{viewport === 'desktop' && renderButtonGroup(countryUrl, navigate, lang, false, currentLabels)}
					</Wrapper>
					{flowId === 0 && renderUpsellProductCards()}
					{viewport === 'mobile' &&
						renderButtonGroup(countryUrl, navigate, lang, miniCartIsOpen, currentLabels)}
					<CartDrawer
						miniCartIsOpen={miniCartIsOpen}
						cartPrice={cartPrice}
						setMiniCartIsOpen={setMiniCartIsOpen}
					/>
					<Drawer
						paddingX="large"
						title={getDrawerTitle('productSelection', currentLabels?.helpDrawers)}
						isOpen={drawerIsOpen}
						setIsOpen={setDrawerIsOpen}>
						<ContentfulDrawerContent id="productSelection" />
					</Drawer>
					<Contact />
				</>
			)}
		</>
	);
};

export const ProductDonePage = withLocalization(ProductDone);
